// src/pages/OrderDetail.js

import React, { useEffect, useState, useContext, useRef } from 'react';
import {
  Typography,
  Paper,
  Grid,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Box,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Card,
  CardContent,
  CardMedia,
  Chip,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { getOrderById, createReturn, createExchange, getReturnHistory, getPaymentHistory } from '../services/orderService';
import { ContentCopy, KeyboardArrowDown, KeyboardArrowUp, ReceiptLong, Download, Print, PictureAsPdf } from '@mui/icons-material';
import ReturnExchangeModal from '../components/ReturnExchangeModal';
import { useLoading } from '../contexts/LoadingContext';
import BackButton from '../components/BackButton';
import { CurrencyContext } from '../contexts/CurrencyContext';
import formatDateToStandard, { DATE_FORMAT_DISPLAY } from '../utils/DateFormatter';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { getCustomerById } from '../services/customerService';
import { OrgContext } from '../contexts/OrgContext';
import { getOrganizationById } from '../services/organizationService'; // Add this import
import profitizeLogo from '../assets/profitize_logo.png'; // Import the logo at the top of the file

const currencySymbols = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  AUD: 'A$',
  CAD: 'C$',
  CHF: 'CHF',
  CNY: '¥',
  INR: '₹',
  JPY: '¥',
  BDT: '৳',
};

const OrderDetail = () => {
  const { orderId } = useParams();
  const { isLoading, setIsLoading } = useLoading();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const { currency } = useContext(CurrencyContext);
  
  // Remove the unused orgId variable
  const { orgLogoURL } = useContext(OrgContext);
  const [logoError, setLogoError] = useState(false);
  
  const [openInvoice, setOpenInvoice] = useState(false);
  const [customerDetails, setCustomerDetails] = useState(null);
  const printRef = useRef();
  const [organization, setOrganization] = useState(null);
  const [openReturnExchange, setOpenReturnExchange] = useState(false);
  const [returnHistory, setReturnHistory] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [returnExchangeMode, setReturnExchangeMode] = useState('return'); // 'return' | 'exchange'

  // Compute availability summary (products & total available units after returns/exchanges)
  const availabilitySummary = React.useMemo(() => {
    if (!order) return { totalProducts: 0, totalAvailable: 0 };
    const qtyMap = new Map();
    // Start with original line items
    order.lineItems?.forEach(li => {
      if (li?.product?.id) {
        qtyMap.set(li.product.id, (qtyMap.get(li.product.id) || 0) + li.quantity);
      }
    });
    // Apply return & exchange history
    returnHistory.forEach(h => {
      h.lineItems?.forEach(li => {
        if (!li?.product?.id) return;
        const pid = li.product.id;
        if (!qtyMap.has(pid)) qtyMap.set(pid, 0);
        if (h.order_type === 'Return') {
          qtyMap.set(pid, qtyMap.get(pid) - Math.abs(li.quantity));
        } else if (h.order_type === 'Exchange') {
          if (li.quantity < 0) {
            qtyMap.set(pid, qtyMap.get(pid) - Math.abs(li.quantity));
          } else if (li.quantity > 0) {
            qtyMap.set(pid, qtyMap.get(pid) + li.quantity);
          }
        }
      });
    });
    let totalProducts = 0;
    let totalAvailable = 0;
    qtyMap.forEach(v => {
      if (v > 0) {
        totalProducts += 1;
        totalAvailable += v;
      }
    });
    return { totalProducts, totalAvailable };
  }, [order, returnHistory]);

  useEffect(() => {
    if (order?.id) {
      getReturnHistory(order.id)
        .then(setReturnHistory)
        .catch(err => console.error('Failed to load return history:', err));
      
      // If order has payments (AR or AP), fetch payment history
      if (order.accounts_receivable || order.accounts_payable) {
        getPaymentHistory(order.id)
          .then(setPaymentHistory)
          .catch(err => console.error('Error fetching payment history:', err));
      }
    }
  }, [order]);

  const loadImage = (src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  };

  const addImageToPDF = async (doc, imgSrc, x, y, maxWidth, maxHeight) => {
    try {
      const img = await loadImage(imgSrc);
      const aspectRatio = img.width / img.height;
      
      let width = maxWidth;
      let height = width / aspectRatio;
      
      if (height > maxHeight) {
        height = maxHeight;
        width = height * aspectRatio;
      }
      
      doc.addImage(img.src, 'PNG', x, y, width, height);
      return height; // Return actual height used
    } catch (err) {
      console.warn('Failed to add image:', err);
      return 0;
    }
  };

  const createBase64ImageFromUrl = async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error converting image to base64:', error);
      return null;
    }
  };

  useEffect(() => {
    if (orderId) {
      setIsLoading(true);
      getOrderById(orderId)
        .then(setOrder)
        .catch(err => setError('Failed to load order details'))
        .finally(() => setIsLoading(false));
    }
  }, [orderId, setIsLoading]);

  useEffect(() => {
    if (order?.customer_id) {
      getCustomerById(order.organization?.id, order.customer_id)
        .then(setCustomerDetails)
        .catch(console.error);
    }
    
    // Fetch organization details if we have an org ID
    if (order?.organization?.id) {
      getOrganizationById(order.organization.id)
        .then(setOrganization)
        .catch(err => console.error('Failed to load organization details:', err));
    }
  }, [order]);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
  };

  // removed unused generateInvoiceContent

  const formatDateForFileName = (date) => {
    return new Date(date).toISOString().split('T')[0]; // Returns YYYY-MM-DD
  };

  const handleDownloadInvoice = () => {
    generatePDF('download');
  };

  const handlePrintInvoice = () => {
    generatePDF('print');
  };

  const formatTaxValue = (value, type = 'amount') => {
    if (!value || !parseFloat(value)) return '--';
    if (type === 'percentage') return `${parseFloat(value)}%`;
    return `${symbol}${parseFloat(value).toFixed(2)}`;
  };

  const calculateAmountWithTax = (order) => {
    if (!order.meta?.tax_amount || !parseFloat(order.meta.tax_amount)) {
      return parseFloat(order.amount || 0);
    }
    
    const amount = parseFloat(order.amount || 0);
    const taxAmount = parseFloat(order.meta.tax_amount);
    return amount + taxAmount; // Always add tax amount
  };

  // Fix: Final price should be unit_price (already discounted), not further discounting it
  const calculateDiscountedPrice = (item) => {
    // The backend already provides unit_price as the final price after discount.
    // So just return parseFloat(item.unit_price)
    return parseFloat(item.unit_price || 0);
  };

  const formatDiscountText = (item) => {
    if (!item.discount_type || item.discount_type === 'none' || !item.discount_value) {
      return '--';
    }
    return item.discount_type === 'percentage' 
      ? `${item.discount_value}%` 
      : `${symbol}${parseFloat(item.discount_value).toFixed(2)}`;
  };

  const handleLogoError = () => {
    console.warn('Organization logo failed to load');
    setLogoError(true);
  };

  const generatePDF = async (action) => {
    const doc = new jsPDF();
    const margin = 15;
    let currentY = margin;
  
    // Add organization logo
    if (orgLogoURL && !logoError) {
      try {
        const base64Logo = orgLogoURL.startsWith('data:') 
          ? orgLogoURL 
          : await createBase64ImageFromUrl(orgLogoURL);
  
        if (base64Logo) {
          const imgHeight = await addImageToPDF(doc, base64Logo, margin, currentY, 60, 30);
          currentY += imgHeight + 10; // Add spacing below the logo
        }
      } catch (err) {
        console.warn('Failed to add logo to PDF:', err);
        currentY += 40; // Add spacing even if the logo fails
      }
    }
  
    // Add organization details
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text('From:', margin, currentY);
    currentY += 7;
    doc.text(order.organization?.organization_name || '', margin, currentY);
    currentY += 7;
  
    if (organization?.meta?.orgAddress) {
      const addressLines = organization.meta.orgAddress.split('\n');
      addressLines.forEach((line) => {
        doc.text(line.trim(), margin, currentY);
        currentY += 7; // Add spacing for each line
      });
    }
  
    if (organization?.meta?.taxNumber) {
      doc.text(`Tax Number: ${organization.meta.taxNumber}`, margin, currentY);
      currentY += 10; // Add spacing after tax number
    }
  
    // Add invoice details
    doc.text(`Invoice No: ${order.id}`, 120, margin);
    doc.text(`Date: ${formatDateToStandard(order.order_date)}`, 120, margin + 7);
    currentY += 20; // Add spacing below invoice details
  
    // Add customer details
    if (customerDetails) {
      doc.text('Bill To:', margin, currentY);
      currentY += 7;
      doc.text(customerDetails.customer_name || '', margin, currentY);
      currentY += 7;
      doc.text(customerDetails.contact_number || '', margin, currentY);
      currentY += 7;
      doc.text('Billing Address:', margin, currentY);
      currentY += 7;
      doc.text(customerDetails.address || '', margin, currentY);
      currentY += 10; // Add spacing below billing address
  
      doc.text('Ship To:', 120, currentY - 28); // Align with "Bill To"
      doc.text(customerDetails.customer_name || '', 120, currentY - 21);
      doc.text(customerDetails.contact_number || '', 120, currentY - 14);
      doc.text('Delivery Address:', 120, currentY - 7);
      const deliveryAddress = order.meta?.deliveryAddress || customerDetails.address || '';
      const deliveryLines = deliveryAddress.split('\n');
      deliveryLines.forEach((line) => {
        doc.text(line.trim(), 120, currentY);
        currentY += 7; // Add spacing for each line
      });
      currentY += 10; // Add spacing below shipping address
    }
  
    // Add line items table
    doc.autoTable({
      startY: currentY,
      head: [['Product', 'SKU', 'Quantity', 'Original Price', 'Discount', 'Final Price', 'Total']],
      body: order.lineItems.map((item) => [
        item.product?.product_name,
        item.product?.sku,
        item.quantity,
        `${symbol}${parseFloat(item.original_unit_price || item.unit_price).toFixed(2)}`,
        formatDiscountText(item),
        `${symbol}${calculateDiscountedPrice(item).toFixed(2)}`,
        `${symbol}${parseFloat(item.total_price).toFixed(2)}`,
      ]),
      styles: { fontSize: 10 },
      headStyles: { fillColor: [66, 66, 66] },
      margin: { top: 10 },
    });
  
    currentY = doc.lastAutoTable.finalY + 10; // Update currentY after the table
  
    // Add summary section
    const summaryX = 140;
    doc.text(`Subtotal: ${symbol}${parseFloat(order.amount || 0).toFixed(2)}`, summaryX, currentY);
    currentY += 7;
  
    if (order.meta?.tax_percentage) {
      doc.text(`Tax (${order.meta.tax_percentage}%): ${formatTaxValue(order.meta.tax_amount)}`, summaryX, currentY);
      currentY += 7;
    }
  
    doc.text(`Final Amount: ${symbol}${calculateAmountWithTax(order).toFixed(2)}`, summaryX, currentY);
    currentY += 10; // Add spacing below the summary
  
    // Add footer
    doc.setFontSize(10);
    doc.setTextColor(128, 128, 128);
    const footerY = doc.internal.pageSize.height - 18;
    doc.text('Powered by', doc.internal.pageSize.width / 2, footerY, { align: 'center' });
  
    try {
      const logoBase64 = await createBase64ImageFromUrl(profitizeLogo);
      if (logoBase64) {
        const logoWidth = 25;
        const logoHeight = logoWidth / (1537 / 188); // Maintain aspect ratio
        const logoX = (doc.internal.pageSize.width - logoWidth) / 2;
        doc.addImage(logoBase64, 'PNG', logoX, footerY + 2, logoWidth, logoHeight);
      } else {
        doc.text('PROFITIZE', doc.internal.pageSize.width / 2, footerY + 5, { align: 'center' });
      }
    } catch (error) {
      console.error('Failed to add Profitize logo to PDF:', error);
      doc.text('PROFITIZE', doc.internal.pageSize.width / 2, footerY + 5, { align: 'center' });
    }
  
    // Save or print the PDF
    if (action === 'download') {
      const fileName = `invoice_${formatDateForFileName(order.order_date)}.pdf`;
      doc.save(fileName);
    } else if (action === 'print') {
      doc.autoPrint();
      window.open(doc.output('bloburl'), '_blank');
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) return <Typography color="error">{error}</Typography>;
  if (!order) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
      <CircularProgress />
    </Box>
  );

  const symbol = currencySymbols[currency] || currency;

  // Add the function to format AR/AP values
  const formatARAPValue = (value) => {
    if (!value) return '--';
    return `${symbol}${parseFloat(value).toFixed(2)}`;
  };

  // Define main fields for the order details section
  const mainFields = {
    'Order ID': order.id,
    'Order Type': order.order_type,
    'Order Date': (
      <Tooltip title={`Format: ${DATE_FORMAT_DISPLAY}`}>
        <span>{formatDateToStandard(order.order_date)}</span>
      </Tooltip>
    ),
    'Order Status': order.order_status,
    'Amount (Before Tax)': `${symbol}${parseFloat(order.amount || 0).toFixed(2)}`,
    'Tax Percentage': formatTaxValue(order.meta?.tax_percentage, 'percentage'),
    'Tax Amount': formatTaxValue(order.meta?.tax_amount),
    'Final Amount': `${symbol}${calculateAmountWithTax(order).toFixed(2)}`,
    'Payment Type': order.payment_type || '--',
    'Payment Due Date': order.payment_due_date 
      ? formatDateToStandard(order.payment_due_date) 
      : '--',
    // Add AR/AP fields if they exist
    ...(order.accounts_receivable ? {
      'Accounts Receivable': formatARAPValue(order.accounts_receivable),
      'AR Payment Due Date': order.meta?.arPaymentDueDate
        ? formatDateToStandard(order.meta.arPaymentDueDate)
        : order.payment_due_date
          ? formatDateToStandard(order.payment_due_date)
          : '--'
    } : {}),
    ...(order.accounts_payable ? {
      'Accounts Payable': formatARAPValue(order.accounts_payable),
      'AP Payment Due Date': order.meta?.apPaymentDueDate
        ? formatDateToStandard(order.meta.apPaymentDueDate)
        : order.payment_due_date
          ? formatDateToStandard(order.payment_due_date)
          : '--'
    } : {}),
    'Organization': order.organization?.organization_name,
  };

  // Also update the PrintContent component to use orgLogoURL
  const PrintContent = React.forwardRef((props, ref) => (
    <Box ref={ref} sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          {orgLogoURL && !logoError && (
            <Box sx={{ width: '200px', height: '60px', position: 'relative' }}>
              <img 
                src={orgLogoURL} 
                alt="Organization Logo" 
                crossOrigin="anonymous"
                style={{ 
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  position: 'absolute',
                  top: '50%',
                  transform: 'translateY(-50%)'
                }} 
                onError={handleLogoError}
              />
            </Box>
          )}
          <Typography variant="h6">
            {order?.organization?.organization_name}
          </Typography>
          {organization?.meta?.orgAddress && (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mt: 2 }}>
              {organization.meta.orgAddress}
            </Typography>
          )}
          {organization?.meta?.taxNumber && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Tax Number: {organization.meta.taxNumber}
            </Typography>
          )}
        </Box>
        <Box>
          <Typography variant="subtitle2">Invoice No: {order.id}</Typography>
          <Typography variant="subtitle2">
            Date: {formatDateToStandard(order.order_date)}
          </Typography>
        </Box>
      </Box>

      {/* Add spacing below the organization details */}
      <Box sx={{ mt: 4 }}>
        <Grid container spacing={4}>
          {customerDetails && (
            <>
              <Grid item xs={6}>
                <Typography variant="subtitle2" gutterBottom>Bill To:</Typography>
                <Typography>{customerDetails.customer_name}</Typography>
                <Typography>{customerDetails.contact_number}</Typography>
                <Typography variant="subtitle2" gutterBottom>Billing Address:</Typography>
                <Typography>{customerDetails.address}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" gutterBottom>Ship To:</Typography>
                <Typography>{customerDetails.customer_name}</Typography>
                <Typography>{customerDetails.contact_number}</Typography>
                <Typography variant="subtitle2" gutterBottom>Delivery Address:</Typography>
                {order.meta?.deliveryAddress ? (
                  <Typography sx={{ whiteSpace: 'pre-line' }}>{order.meta.deliveryAddress}</Typography>
                ) : (
                  <Typography>{customerDetails.address}</Typography>
                )}
              </Grid>
            </>
          )}
        </Grid>
      </Box>

      <TableContainer sx={{ mt: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Product</TableCell>
              <TableCell>SKU</TableCell>
              <TableCell>Quantity</TableCell>
              <TableCell>Original Price</TableCell>
              <TableCell>Discount</TableCell>
              <TableCell>Final Price</TableCell>
              <TableCell>Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {order.lineItems.map((item) => {
              console.log('LineItem object:', item);
              return (
                <TableRow key={item.id}>
                  <TableCell>{item.product?.product_name}</TableCell>
                  <TableCell>{item.product?.sku}</TableCell>
                  <TableCell>{item.quantity}</TableCell>
                  {(() => { console.log('original_unit_price:', item.original_unit_price, 'unit_price:', item.unit_price, typeof item.unit_price); return (<TableCell>{symbol}{parseFloat(item.original_unit_price || item.unit_price || 0).toFixed(2)}</TableCell>); })()}
                  <TableCell>{formatDiscountText(item)}</TableCell>
                  {(() => { console.log('total_price:', item.total_price, typeof item.total_price); return (<TableCell>{symbol}{parseFloat(item.total_price || 0).toFixed(2)}</TableCell>); })()}
                  {(() => { console.log('total_price (2nd col):', item.total_price, typeof item.total_price); return (<TableCell>{symbol}{parseFloat(item.total_price || 0).toFixed(2)}</TableCell>); })()}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 4, textAlign: 'right' }}>
        <Typography>Subtotal: {symbol}{parseFloat(order.amount || 0).toFixed(2)}</Typography>
        {order.meta?.tax_percentage && (
          <Typography>
            Tax ({order.meta.tax_percentage}%): {formatTaxValue(order.meta.tax_amount)}
          </Typography>
        )}
        <Typography variant="h6">
          Final Amount: {symbol}{calculateAmountWithTax(order).toFixed(2)}
        </Typography>
        
        {/* Add AR/AP information to the invoice */}
        {order.accounts_receivable && (
          <Typography sx={{ mt: 2, color: 'info.main' }}>
            Accounts Receivable: {formatARAPValue(order.accounts_receivable)}
          </Typography>
        )}
        {order.accounts_payable && (
          <Typography sx={{ mt: 2, color: 'info.main' }}>
            Accounts Payable: {formatARAPValue(order.accounts_payable)}
          </Typography>
        )}
        {(order.accounts_receivable || order.accounts_payable) && (
          <Typography sx={{ fontSize: '0.85rem', fontStyle: 'italic' }}>
            Payment Due: {order.meta?.arPaymentDueDate || order.meta?.apPaymentDueDate 
              ? formatDateToStandard(order.meta.arPaymentDueDate || order.meta.apPaymentDueDate)
              : order.payment_due_date
                ? formatDateToStandard(order.payment_due_date)
                : '--'}
          </Typography>
        )}
      </Box>

      <Box sx={{ mt: 6, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">
          Powered by
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          mt: 1
        }}>
          <img 
            src={profitizeLogo} 
            alt="PROFITIZE" 
            style={{ 
              height: '15px', // Reduced by ~80% from previous 20px
              width: 'auto'   // Maintain aspect ratio
            }}
            onError={(e) => {
              e.target.style.display = 'none';
              // Add text fallback if image fails to load
              e.target.parentNode.innerHTML += '<Typography variant="body1" fontWeight="bold">PROFITIZE</Typography>';
            }}
          />
        </Box>
      </Box>
    </Box>
  ));

  // Add this function to handle opening the attachment in a new tab
  const handleOpenAttachment = () => {
    if (order.meta?.attachment?.fileUrl) {
      window.open(order.meta.attachment.fileUrl, '_blank');
    }
  };

  const canReturnExchange = (order) => {
    if (!order) return false;
    if (order.order_type !== 'Sale') return false; // only Sales support returns/exchanges
    if (order.order_status === 'Cancelled') return false;
    if (order.order_type === 'Return' || order.order_type === 'Exchange') return false;
    return order.lineItems && order.lineItems.length > 0;
  };

  const handleReturnExchangeSubmit = async (type, data) => {
    try {
      if (type === 'exchange') {
        // Ensure exchanged_items fields are correct types
        const exchanged_items = data.exchanged_items.map(item => ({
          ...item,
          unit_price: parseFloat(item.unit_price),
          original_unit_price: item.original_line_item_id !== undefined ? parseFloat(item.original_unit_price) : undefined,
          original_line_item_id: item.original_line_item_id !== undefined ? parseInt(item.original_line_item_id, 10) : 0,
          discount_value: item.discount_value !== undefined && item.discount_value !== null && item.discount_value !== '' ? parseFloat(item.discount_value) : undefined,
        }));
        const payload = {
          ...data,
          exchanged_items,
        };
        console.log('Exchange payload:', payload); // Debugging
        await createExchange(order.id, payload);
      } else {
        // Ensure returned_items fields are correct types
        const returned_items = data.returned_items.map(item => ({
          ...item,
          unit_price: parseFloat(item.unit_price),
          original_unit_price: item.original_line_item_id !== undefined ? parseFloat(item.original_unit_price) : undefined,
          original_line_item_id: item.original_line_item_id !== undefined ? parseInt(item.original_line_item_id, 10) : 0,
          discount_value: item.discount_value !== undefined && item.discount_value !== null && item.discount_value !== '' ? parseFloat(item.discount_value) : undefined,
        }));
        const payload = {
          ...data,
          returned_items,
        };
        console.log('Return payload:', payload); // Debugging
        await createReturn(order.id, payload);
      }
      
      const updatedOrder = await getOrderById(order.id);
      setOrder(updatedOrder);
      
      const updatedHistory = await getReturnHistory(order.id);
      setReturnHistory(updatedHistory);
      
    } catch (error) {
      console.error(`Error processing ${type}:`, error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 3, bgcolor: 'background.paper', color: 'text.primary', backdropFilter: 'saturate(140%) blur(6px)', border: '1px solid', borderColor: 'divider' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1.5, sm: 0 },
          mb: 3,
        }}
      >
        <BackButton />
        <Box
          sx={{
            width: { xs: '100%', sm: 'auto' },
            overflowX: { xs: 'auto', sm: 'visible' },
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{
              minWidth: { xs: 720, sm: 'auto' },
              py: { xs: 0.5, sm: 0 },
            }}
          >
          {canReturnExchange(order) && (
            <>
              <Button
                variant="contained"
                color="warning"
                onClick={() => { setReturnExchangeMode('return'); setOpenReturnExchange(true); }}
                size="small"
                sx={{ px: { xs: 1.5, sm: 3 } }}
              >
                Return Items
              </Button>
              <Button
                variant="outlined"
                color="warning"
                onClick={() => { setReturnExchangeMode('exchange'); setOpenReturnExchange(true); }}
                sx={{
                  px: { xs: 1.5, sm: 3 },
                  borderColor: '#ff9800',
                  color: '#ff9800',
                  '&:hover': { borderColor: '#f57c00', backgroundColor: 'rgba(255,152,0,0.05)' }
                }}
                size="small"
              >
                Exchange Items
              </Button>
              <Chip
                label={`Avail: ${availabilitySummary.totalAvailable} (Products: ${availabilitySummary.totalProducts})`}
                color="info"
                variant="outlined"
                sx={{ alignSelf: { xs: 'center', sm: 'center' } }}
              />
            </>
          )}
          {order.order_type === 'Sale' && (
            <>
              <Button
                startIcon={<ReceiptLong />}
                variant="contained"
                onClick={() => setOpenInvoice(true)}
                sx={{
                  backgroundColor: '#2196f3',
                  '&:hover': { backgroundColor: '#1976d2' },
                  px: { xs: 1.5, sm: 3 }
                }}
                size="small"
              >
                View Invoice
              </Button>
              <Button
                startIcon={<Download />}
                variant="contained"
                onClick={handleDownloadInvoice}
                sx={{
                  backgroundColor: '#4caf50',
                  '&:hover': { backgroundColor: '#388e3c' },
                  px: { xs: 1.5, sm: 3 }
                }}
                size="small"
              >
                Download Invoice
              </Button>
            </>
          )}
          </Stack>
        </Box>
      </Box>
      <Typography variant="h5" gutterBottom>Order Details</Typography>

      {/* Main Fields Grid */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {Object.entries(mainFields).map(([key, value]) => (
          <React.Fragment key={key}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {key}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={5} md={7}>
              <Typography variant="body1">{value}</Typography>
            </Grid>
            <Grid item xs={12} sm={1} md={1} display="flex" alignItems="center" justifyContent="flex-end">
              <Tooltip title="Copy">
                <IconButton onClick={() => handleCopy(value)}>
                  <ContentCopy />
                </IconButton>
              </Tooltip>
            </Grid>
          </React.Fragment>
        ))}
      </Grid>

      {/* Document Preview Section */}
      {order.meta?.attachment && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>Document</Typography>
          <Card sx={{ maxWidth: 400, backgroundColor: 'background.paper' }}>
            {order.meta.attachment.fileType.startsWith('image/') ? (
              <CardMedia
                component="img"
                height="200"
                image={order.meta.attachment.fileUrl}
                alt={order.meta.attachment.fileName}
                sx={{
                  objectFit: 'contain',
                  bgcolor: 'white',
                  cursor: 'pointer'
                }}
                onClick={handleOpenAttachment}
              />
            ) : (
              <Box
                sx={{
                  height: 120,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  p: 2
                }}
                onClick={handleOpenAttachment}
              >
                <PictureAsPdf sx={{ fontSize: 60, color: '#f44336', mb: 1 }} />
                <Typography variant="body2" align="center">
                  {order.meta.attachment.fileName}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1 }}
                  onClick={handleOpenAttachment}
                >
                  Open PDF
                </Button>
              </Box>
            )}
            <CardContent sx={{ py: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {order.meta.attachment.fileName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Click above to view
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Return/Exchange History */}
      {returnHistory.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>Return &amp; Exchange History</Typography>
          <TableContainer component={Paper} sx={{ backgroundColor: 'background.paper' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {returnHistory.map((historyItem) => (
                  <TableRow key={historyItem.id}>
                    <TableCell>
                      {formatDateToStandard(historyItem.order_date)}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={historyItem.order_type}
                        color={historyItem.order_type === 'Return' ? 'error' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {currencySymbols[currency] || currency}
                      {Math.abs(parseFloat(historyItem.amount)).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={historyItem.order_status}
                        color="success"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {historyItem.meta?.return_reason || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Payment History */}
      {paymentHistory.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>Payment History</Typography>
          <TableContainer component={Paper} sx={{ backgroundColor: 'background.paper' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Payment ID</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paymentHistory.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.id}</TableCell>
                    <TableCell>
                      {formatDateToStandard(item.payment_date)}
                    </TableCell>
                    <TableCell>
                      {currencySymbols[currency] || currency}
                      {parseFloat(item.amount).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={item.payment_type === 'ar' ? 'Accounts Receivable' : 'Accounts Payable'}
                        color={item.payment_type === 'ar' ? 'primary' : 'info'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={item.is_partial ? 'Partial' : 'Full'}
                        color={item.is_partial ? 'warning' : 'success'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Line Items Table */}
      <Typography variant="h6" gutterBottom>Line Items</Typography>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>ID</TableCell>
              <TableCell>Product Name</TableCell>
              <TableCell>SKU</TableCell>
              <TableCell>Quantity</TableCell>
              <TableCell>
                {order.order_type === "Purchase" ? "Unit Cost" : "Unit Price"}
              </TableCell>
              <TableCell>
                {order.order_type === "Purchase" ? "Total Cost" : "Total Price"}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {order.lineItems.map((item) => (
              <ExpandableRow
                key={item.id}
                item={item}
                onCopy={handleCopy}
                calculateDiscountedPrice={calculateDiscountedPrice}
                formatDiscountText={formatDiscountText}
                orderType={order.order_type} // Pass orderType as prop
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={openInvoice}
        onClose={() => setOpenInvoice(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Invoice</Typography>
          <Button
            startIcon={<Print />}
            variant="contained"
            onClick={handlePrintInvoice}
            sx={{
              backgroundColor: '#ff9800',
              '&:hover': { backgroundColor: '#f57c00' }
            }}
          >
            Print Invoice
          </Button>
        </DialogTitle>
        <DialogContent>
          <PrintContent ref={printRef} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenInvoice(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Return/Exchange Modal */}
      <ReturnExchangeModal
        open={openReturnExchange}
        onClose={() => setOpenReturnExchange(false)}
        order={order}
        onSubmit={handleReturnExchangeSubmit}
        returnHistory={returnHistory}
        mode={returnExchangeMode}   // renamed from initialMode
      />
    </Paper>
  );
};

// Expandable Row Component
const ExpandableRow = ({ item, onCopy, calculateDiscountedPrice, formatDiscountText, orderType }) => {
  const [open, setOpen] = useState(false);
  const { currency } = useContext(CurrencyContext);

  const symbol = currencySymbols[currency] || currency;

  // Set labels based on orderType
  const unitLabel = orderType === "Purchase" ? "Unit Cost" : "Unit Price";
  const totalLabel = orderType === "Purchase" ? "Total Cost" : "Total Price";

  const detailFields = {
    'Product Category': item.product?.category,
    'Product Sub Category': item.product?.sub_category,
    'Type of Inventory': item.product?.type_of_inventory,
    'Unit of Measure': item.product?.unit_of_measure,
    'Company': item.product?.company,
    'SKU': item.product?.sku,
    'Scanner Reference': item.product?.scanner_reference,
    'Current Unit Cost': `${symbol}${parseFloat(item.product?.current_unit_cost || 0).toFixed(2)}`,
    'Current Unit Price': `${symbol}${parseFloat(item.product?.current_unit_price || 0).toFixed(2)}`,
    'Date': (
      <Tooltip title={`Format: ${DATE_FORMAT_DISPLAY}`}>
        <span>{formatDateToStandard(item.date_modified || item.product?.date_modified)}</span>
      </Tooltip>
    ),
    'Original Price': `${symbol}${parseFloat(item.original_unit_price || item.unit_price || 0).toFixed(2)}`,
    'Discount': formatDiscountText(item),
    [`${unitLabel}`]: `${symbol}${calculateDiscountedPrice(item).toFixed(2)}`,
    [`${totalLabel}`]: `${symbol}${parseFloat(item.total_price).toFixed(2)}`,
  };

  return (
    <>
      <TableRow>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>{item.id}</TableCell>
        <TableCell>{item.product?.product_name}</TableCell>
        <TableCell>{item.product?.sku}</TableCell>
        <TableCell>{item.quantity}</TableCell>
        <TableCell>{symbol}{parseFloat(item.unit_price).toFixed(2)}</TableCell>
        <TableCell>{symbol}{parseFloat(item.total_price).toFixed(2)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="h6" gutterBottom component="div">
                Product Details
              </Typography>
              <Table size="small">
                <TableBody>
                  {Object.entries(detailFields).map(([key, value]) => (
                    value && (
                      <TableRow key={key}>
                        <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                          {key}
                        </TableCell>
                        <TableCell>{value}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Copy">
                            <IconButton onClick={() => onCopy(value)}>
                              <ContentCopy />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    )
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default OrderDetail;
